// Utils
import Dictionary from '@/types/dictionary';
import { REGISTERED_ACCOUNT_TYPES } from '../constants';
import { LOCALE } from '../i18n';

export enum BMO_CONTACT_FIELDS {
  ANNUITANT_FIRST_NAME = 'fname',
  ANNUITANT_LAST_NAME = 'lname',
  POLICYOWNER_FIRST_NAME = 'fnamePolicyOwner',
  POLICYOWNER_LAST_NAME = 'lnamePolicyOwner',
  SECOND_POLICYOWNER_FIRST_NAME = 'fnameSecondPolicyOwner',
  SECOND_POLICYOWNER_LAST_NAME = 'lnameSecondPolicyOwner',
  OWNER_IS_ANNUITANT = 'ownerIsAnnuitant',
  DOB = 'dob',
  PROVINCE = 'province',
  ACCOUNT_TYPE = 'accountType',
  REGISTERED_ACCOUNT_TYPE = 'registeredAccountType',
  SHOW_SECOND_POLICYOWNER = 'showSecondPolicyOwner', // Not saved
}

export const BMO_GIF_LSEG_UNIVERSE = 'Insurance Funds';
export const BMO_GIF_LSEG_MANUFACTURERS = ['BMO Asset Management Inc', 'BMO Asset Management Inc.', 'BMO Investments Inc'];

export const POLICYOWNER_OPTIONS: Dictionary<boolean> = {
  YES: true,
  NO: false,
};

export const BMO_CONTACT_TYPE = 'BMOContact';

export const MIN_PRESTIGE_DEPOSIT = 250000;

export const GIF_PORTFOLIO_COLOR = '#0587da';

export const GICBenchmarkSymbol = '5YGIC';

export const BMOFormErrorKeys = {
  INVALID_DATE: 'INVALID_DATE',
  DOB_REQUIRED: 'DOB_REQUIRED',
  MAX_AGE_90: 'MAX_AGE_90',
  INVALID_ACCOUNT_TYPE_AGE_71: 'INVALID_ACCOUNT_TYPE_AGE_71',
  MISSING_REGISTERED_ACCOUNT: 'MISSING_REGISTERED_ACCOUNT',
  ACCOUNT_TYPE_REQUIRED: 'ACCOUNT_TYPE_REQUIRED',
  MISSING_POLICYOWNER_FNAME: 'MISSING_POLICYOWNER_FNAME',
  MISSING_POLICYOWNER_LNAME: 'MISSING_POLICYOWNER_LNAME',
};

export const BMOi18n = {
  [LOCALE.EN_CA]: {
    ACCOUNT_TYPE: 'Account type',
    SELECT_CLIENT: 'Select existing Client',
    CREATE_CLIENT: 'Create a new Client',
    EDIT_CLIENT: 'Edit Client',
    EDIT_SUCCESS: 'Your client was successfully updated',
    EMPTY_STATE: {
      TITLE: 'No client allocated',
      DESCRIPTION: 'Please assign a client to the comparison.',
    },
    CREATE_SUCCESS: 'Your client was successfully created',
    ANNUITANT: 'Annuitant',
    POLICYOWNER_1: 'Policyowner #1',
    POLICYOWNER_2: 'Policyowner #2',
    FIRST_NAME: 'First Name',
    LAST_NAME: 'Last Name',
    ANNUITANT_FIRST_NAME: 'Annuitant First Name',
    ANNUITANT_LAST_NAME: 'Annuitant Last Name',
    POLICYOWNER_FIRST_NAME: 'Policyowner First Name',
    POLICYOWNER_LAST_NAME: 'Policyowner Last Name',
    POLICYOWNER_ANNUITANT_OPTION: 'Policyowner is the Annuitant',
    DOB: 'Date of Birth',
    SECOND_DOB: 'Spouse Date of Birth',
    AGE: 'Age',
    SECOND_AGE: 'Spouse Age',
    AGE_TOOLTIP: {
      TITLE: 'Joint Age',
      TEXT: 'Any age requirements are based on the younger of the two birthdays',
    },
    LIF_MAX_DISABLED_QC: 'LIF Max is disabled for QC residents since there are no upper limits on LIF withdrawals as of 2025.',
    RATE_OF_RETURN: 'Gross Rate of Return',
    AGE_CALCULATION_INFO: 'Age is calculated based on the purchase date',
    ADD_SECOND_POLICYOWNER: 'Add Owner',
    ACCOUNT_DETAILS: 'Account Details',
    SELECT_REGISTERED_ACCOUNT: 'Select Account Type*',
    SELECT_PROVINCE: 'Select Province',
    PROVINCE: 'Province',
    RIF: 'RIF (Retirement Income Fund)',
    LIF: 'LIF (Life Income Fund)',
    LRIF: 'LRIF (Locked-in Retirement Income Fund)',
    PRIF: 'PRIF (Prescribed Registered Retirement Income Fund)',
    RLIF: 'RLIF (Registered Life Income Fund)',
    RSP: 'RSP (Registered Saving Plan )',
    LIRA: 'LIRA (Locked-in Retirement Account)',
    LRSP: 'LRSP (Locked-in Registered Saving Plan)',
    RLSP: 'RLSP (Restricted Locked-in Saving Plan)',
    INVALID_DATE: 'Invalid Date',
    DOB_REQUIRED: 'Date of Birth is required',
    MAX_AGE_90: 'Age exceeds maximum of 90',
    ACCOUNT_TYPE_REQUIRED: 'Account type is required',
    INVALID_ACCOUNT_TYPE_AGE_71: 'Maximum age of 71 for RSP, LIRA, LRSP, and RLSP account types',
    MISSING_REGISTERED_ACCOUNT: 'Please select an account type',
    MISSING_POLICYOWNER_FNAME: 'First name is required',
    MISSING_POLICYOWNER_LNAME: 'Last name is required',
    CLIENT_INFO: 'Client Info',
    PURCHASE_AGE: 'Purchase Age:',
    PLAN_OPTIONS: 'Plan Options:',
    GIF7575: 'GIF 75/75',
    GIF75100: 'GIF 75/100',
    GIF75100PLUS: 'GIF 75/100 Plus',
    GIF100100: 'GIF 100/100',
    GIF75100PLUSINFO:
      '75/100 Plus is available on Front-End Load (FEL) at 0% deposit commission and Class F (nominee) sales charge option, and four ETF portfolio GIFs (Fixed Income, Income, Conservative, Balanced) and money market.',
    TERM: 'Term:',
    INVALID_TERM: 'Maturity date exceeds year Annuitant turns age 100',
    DEATHGUARANTEE: 'Death Guarantee Reset Option',
    DEATHGUARANTEEINFO: 'Additional Fees apply',
    DEATHGUARANTEEENHANCED: 'Death Guarantee Reset Enhancement',
    DEATHGUARANTEEENHANCEDINFO: 'Additional Fees apply',
    CLASS_TYPE: 'Class Type:',
    CLASS_A: 'Class A',
    CLASS_A_PRESTIGE: 'Class A Prestige',
    CLASS_F: 'Class F',
    CLASS_F_PRESTIGE: 'Class F Prestige',
    CUSTOM: 'Custom',
    PRESTIGE_INFO: 'To qualify for Prestige, your client must hold at least $250,000 in one or more BMO GIF Contracts issued in their name',
    PORTFOLIO_TYPE: 'Portfolio Type:',
    EIGHTY_TWENTY: '80/20 (Equity/Fixed Income)',
    SIXTY_FORTY: '60/40 (Equity/Fixed Income)',
    FIFTY_FIFTY: '50/50 (Equity/Fixed Income)',
    FORTY_SIXTY: '40/60 (Equity/Fixed Income)',
    TWENTY_EIGHTY: '20/80 (Equity/Fixed Income)',
    INCOMPLETE_PORTFOLIO_TITLE: 'Your portfolio is empty.',
    INCOMPLETE_PORTFOLIO_BODY: 'Choose a plan, class, and portfolio type above to start adding BMO insurance funds',
    EMPTY_PORTFOLIO: 'You currently do not have any holdings inside the portfolio.',
    QUICK_SEACH_PLACEHOLDER: 'Filter BMO Insurance funds by name or code',
    ADD_HOLDING: 'Add BMO Insurance Fund',
    VIEW_GRAPH: 'View Graph',
    CLASS_TYPE_DISABLED: 'Please select a plan option',
    PORTFOLIO_TYPE_DISABLED: 'Please select a plan option and class type',
    ADDITIONAL_INFO: 'Additional Information',
    ADDITIONAL_INFO_EMPTY: 'Create a portfolio to add additional information',
    INITIAL_LUMP_SUM: 'Initial Lump Sum',
    INITIAL_PAD: 'Initial PAD',
    INVALID_AMOUNT: 'Invalid Amount',
    INVALID_DEPOSIT_AGE: 'Initial/subsequent deposit date(s) exceed age limits for account type or guarantee option.',
    INVALID_PRESTIGE_AMOUNT:
      'Note: To qualify for Prestige pricing, $250,000 is required.  For illustration purposes, you can use a value below this threshold.',
    INVALID_NON_PRESTIGE_AMOUNT:
      'Note: An amount over $250,000 qualifies for Prestige pricing. For illustration purposes, you can use a value above this threshold.',
    INVALID_DEPOSIT_AMOUNT: 'Please enter a valid deposit amount',
    PURCHASE_DATE: 'Purchase Date',
    SUBSEQUENT_DEPOSIT: 'Subsequent Deposit',
    YEAR_CALCULATION: 'Year Calculation:',
    IN_AGE: 'Age',
    FROM_AGE: 'From',
    TO_AGE: 'To',
    IN_YEARS: 'In Years',
    NUMBER_OF_YEARS: 'For Number of Years',
    START_YEAR: 'Start in Year',
    START_MONTH: 'Start in Month',
    END_MONTH: 'End in Month',
    FREQUENCY: 'Frequency',
    SELECT_FREQUENCY: 'Select Frequency',
    WEEKLY: 'Weekly',
    BI_WEEKLY: 'Bi-weekly',
    MONTHLY: 'Monthly',
    SEMI_ANNUALLY: 'Semi-annually',
    ANNUALLY: 'Annually',
    QUARTERLY: 'Quarterly',
    VOLATILITY: 'Volatility',
    YES: 'Yes',
    NO: 'No',
    SELECT_PROJECTION_BASIS: 'Select Projection Basis',
    'S&P 500': 'SP500/Bond',
    'SPTSX Composite': 'SPTSX/Bond',
    'MSCI EAFE': 'MSCI EAFE/Bond',
    'DEX Mid Fed': 'DEX/Bond',
    Custom: 'Custom',
    SALES_CHARGES: 'Sales Charges',
    FEL: 'Front-end load',
    DSC: 'Deferred sales charge',
    NL3: 'Chargeback (2)',
    NL5: 'Chargeback (5)',
    ROR_AMOUNT_INFO: 'Enter an amount between 0% - 12%',
    SALES_CHARGE_AMOUNT_INFO: 'Enter an amount between 0% - 5%',
    INVALID_FROM_AGE: "From Age must be greater than or equal to the client's purchase age",
    INVALID_TO_AGE: 'To Age must be greater than the From Age',
    INVALID_START_YEAR: 'Start Year must be less or equal to the term length',
    INVALID_START_MONTH: 'Start month must be less or equal to the end month',
    INVALID_END_MONTH: "End month can't be greater than 12",
    SCENARIOS: {
      ADVERSE_SCENARIO: 'Adverse scenario (-2%)',
      LUMP_SUM_WITHDRAWAL: 'LUMP SUM WITHDRAWAL',
      SYSTEMATIC_WITHDRAWAL: 'SYSTEMATIC WITHDRAWAL',
      PORTFOLIO_VALUE: 'Portfolio Value',
      NET_WITHDRAWAL: 'Net Withdrawal',
      SUBSEQUENT_DEPOSIT: 'Subsequent Deposit',
      NET_DEPOSIT: 'Net Deposit',
      MATURITY_GUARANTEE: 'Maturity Guarantee',
      DEATH_BENEFIT_GUARANTEE: 'Death Benefit Guarantee',
      AGE: 'Age',
      WITHDRAWAL_SCENARIOS: 'Withdrawal Scenarios',
      EMPTY_WITHDRAWAL_SCENARIOS: "Click the '+' at the top of the portfolio table to add a new scenario",
      CREATE_SCENARIO: 'Add Withdrawal Scenario',
      CREATE_DISABLED: 'Reached maximum of 5 scenarios',
      SCENARIO: 'Scenario',
      INCOMPLETE: 'Incomplete',
      EDIT_SCENARIO: 'Edit Scenario',
      DELETE_SCENARIO: 'Delete Scenario',
      EMPTY_STATE_TITLE: 'Withdrawal Scenarios',
      EMPTY_STATE_MESSAGE: 'Complete all the information above to start adding withdrawal scenarios.',
    },
    EDIT_SCENARIO: {
      LUMP_SUM_WITHDRAWAL: 'LUMP SUM WITHDRAWAL',
      SYSTEMATIC_WITHDRAWAL: 'SYSTEMATIC WITHDRAWAL',
      MONTH: 'Month',
      LUMP_SUM_AMOUNT: 'Amount',
      TYPE: 'Type',
      SYSTEMATIC_AMOUNT: 'Amount',
      IN_YEAR: 'In Year',
      AT_AGE: 'At Age',
      START_AGE: 'From Age',
      TO_AGE: 'To',
      FREQUENCY: 'Frequency',
      APPLY: 'Apply',
      INVALID_AGE: "Age must be greater than or equal to client's purchase age",
      INVALID_AGE_65: 'Age must be greater than or equal to 65',
      INVALID_AGE_CUSTOM: 'Age must be greater than or equal to {value}',
      INVALID_END_AGE: 'To Age must be greater than From Age',
    },
    AGE_AND_YEARS: {
      AGE: 'age',
      TO: 'to',
      FOR: 'for',
      YEARS: 'years',
      IN_YEAR: 'In year',
      STARTING_AGE: 'Starting age',
      IN_MONTH: 'in month',
      MONTH: 'month',
      FROM_MONTH: 'from month',
    },
    WITHDRAWAL_TYPES: {
      RRIF: 'RRIF/LIF Minimum',
      LIF_MAX: 'LIF Maximum',
      CUSTOM: 'Custom',
    },
    HIDE_CONTENT_OVERLAY: {
      TITLE: 'Portfolio not fully allocated',
      MESSAGE: 'Please make sure your portfolio is 100% allocated before proceeding',
    },
    GIF_GRAPH: {
      EDIT_DETAILS: 'Edit Details',
      EMPTY_STATE_TITLE: 'Choose a scenario',
      EMPTY_STATE_MESSAGE: 'Select a scenario below to view the graph',
      LEGEND: {
        MATURITY_GUARANTEE: 'At Maturity: 75% on deposits, less a proportionate amount for withdrawals.',
        MATURITY_GUARANTEE_100100:
          'At Maturity: 100% on deposits made at least 15 years, and 75% on deposits made less than 15 years, from the Maturity Date. Automatic monthly resets of the Maturity Guarantee Amount occur up to and including 10 years from the Maturity Date.',
        DEATH_BENEFIT_GUARANTEE_7575: 'At Death: 75% on deposits, less a proportionate amount for withdrawals.',
        DEATH_BENEFIT_GUARANTEE_75100:
          'At Death: 100% on deposits made before the Annuitant is age 80 and 75% on deposits made on or after age 80, less a proportionate amount for withdrawals. Automatic resets of the Death Guarantee Amount occur on every 3rd policy anniversary up to and including the last policy anniversary before the Annuitants 80th birthday.',
        DEATH_BENEFIT_GUARANTEE_75100PLUS:
          'At Death: 100% on deposits made before the Annuitant is age 85 and 75% on deposits made on or after age 85, less a proportionate amount for withdrawals.',
        DEATH_BENEFIT_GUARANTEE_100100:
          'At Death: 100% on deposits made before the Annuitant is age 80 and 75% on deposits made on or after age 80, less a proportionate amount for withdrawals. If the optional Death Guarantee Reset Option is selected, automatic resets of the Death Guarantee Amount occur on every 3rd policy anniversary up to and including the last policy anniversary before the Annuitants 80th birthday. Additional fee applies.',
      },
      DEPOSIT: 'Subsequent Deposits are net of fees.',
    },
    PROPOSAL: {
      GIF_ILLUSTRATION: 'GIF Illustration',
      TITLE: 'BMO Guaranteed Investment Funds',
      HEADERS: {
        ACCOUNT_TYPE: 'Account Type',
        PERSONAL_DATA: 'Personal Data',
        DEPOSIT_DETAILS: 'Initial Deposit Details',
        INVESTMENT_ASSUMPTIONS: 'Investment Assumptions',
        ANNUITANT_NAME: 'Annuitant Name',
        DOB: 'Date of Birth',
        POLICYOWNERS: 'Policyowners',
        AGE: 'Age of Purchase',
        AMOUNT: 'Amount',
        PURCHASE_DATE: 'Purchase Date',
        GUARANTEE_LEVEL: 'Guarantee Level',
        CLASS: 'Class',
        FUNDS: 'Funds',
        MER: 'MER',
        DGRO: 'DGRO Fee',
        TERM: 'Term',
        MATURITY: ' Maturity',
        DEATH_BENEFIT_RESET: 'Death Benefit Reset',
        SALES_CHARGE: 'Sales Charge Option',
        FUND_RETURN: 'Fund Return',
        WITHDRAWAL_AMOUNT: 'Withdrawal Amount',
        LUMP_SUM_WITHDRAWAL_AMOUNT: 'Lump Sum Withdrawal Amount',
        SYSTEMATIC_WITHDRAWAL_AMOUNT: 'Systematic Withdrawal Amount',
        DATE: 'Date',
        ROR: 'Rate of Return*',
      },
      GIF7575: '75/75',
      GIF75100: '75/100',
      GIF75100PLUS: '75/100 Plus',
      GIF100100: '100/100',
      CLASS_A: 'Class A',
      CLASS_A_PRESTIGE: 'Class A Prestige**',
      CLASS_F: 'Class F',
      CLASS_F_PRESTIGE: 'Class F Prestige**',
      FEL: 'Front-end load at',
      DSC: 'Deferred sales charge',
      NL3: 'Chargeback (2)',
      NL5: 'Chargeback (5)',
      DGR_ENHANCED: ' (with Enhancement)',
      PRESTIGE_DISCLAIMER:
        '** Must have a minimum of $250,000 of eligible assets to qualify for Prestige Class.  Eligible assets include segregated fund contracts issued by BMO Insurance held in the client’s name.',
      BASE_SCENARIO: 'Base Scenario',
      EQUITY: 'Equity',
      BOND: 'Bond',
      NONE: 'None',
      OTHERS: 'Others',
      DISCLAIMER:
        'This illustration powered by CapIntel Inc. is not a contract. If there is any discrepancy between an illustration provided to you and your contract with BMO Insurance, your contract governs. Underlying fund data including but not limited to historical returns is provided to BMO Insurance by CapIntel Inc., a provider of financial technology, from third party sources (including Refinitiv, Fundata and Barcharts) and its accuracy cannot be guaranteed.',
      ROR_DISCLAIMER:
        '* Rate of Return is for projection purposes only and is based on a selected flat rate or the Equity and Fixed Income portions as derived from the selected BMO GIF funds and the historical returns of the chosen market index. Projected rate of return is net of fees. Historical returns are not indicative of future performance. Illustrated values are not guaranteed and should not be unduly relied upon. Illustration projections are to help demonstrate the functionality of BMO GIF features. Any amount that is allocated to a segregated fund is invested at the risk of the policy owner and may increase or decrease in value. BMO Life Assurance Company is the sole issuer and guarantor of the BMO GIF individual variable insurance contract. This document provides general information. Please consult the Policy Provisions and Information Folder for details of BMO GIF.',
      MARKETING_TEMPLATE: {
        DISCLAIMERS: {
          FUND_CONSTRUCTION: {
            BACKTESTED:
              '* Source: CapIntel, as at {{date}}. Past returns are not indicative of future performance. Estimated BMO GIF fund returns were calculated using the net returns of {{fundName}} and reducing the net returns by the difference between the MER of the underlying fund and the current MER of the {{gifName}}. Inception date: {{gifName}} – {{inception}}.',
            REGULAR:
              '* Source: CapIntel, as at {{date}}. Past returns are not indicative of future performance. Historical returns of {{gifName}} used. Inception date of {{gifName}} – {{inception}}.',
          },
          BENCHMARK: {
            GIC: 'Fund performance compared to an historical GIC rate over the past 5 years. Source: CapIntel.',
            MANUAL_RATE: 'Fund performance compared to a selected annual rate of {{rate}}%.',
          },
          GIF100100:
            'GIF 100/100: Automatic monthly resets of the Maturity Guarantee Amount occur up to and including 10 years from the Maturity Date. Not all funds available in GIF 100/100.',
          GENERIC:
            'Any amount that is allocated to a segregated fund is invested at the risk of the policy owner. BMO Life Assurance Company is the sole issuer and guarantor of the BMO GIF individual variable insurance contract. This document provides general information. Please consult the Policy Provisions and Information Folder for details of BMO GIF.',
        },
      },
    },
    VERSION_NUMBER: 'BMOGIF V5.0/25-12',
  },
  [LOCALE.FR_CA]: {
    ACCOUNT_TYPE: 'Type de compte',
    SELECT_CLIENT: 'Sélectionnez un client existant',
    CREATE_CLIENT: 'Créer un nouveau client',
    EDIT_CLIENT: 'Modifier un client',
    EDIT_SUCCESS: 'Votre client a été modifié avec succès',
    EMPTY_STATE: {
      TITLE: 'Aucun client assigné',
      DESCRIPTION: 'Veuillez assigner un client à la comparaison.',
    },
    CREATE_SUCCESS: 'Votre client a été créé avec succès',
    ANNUITANT: 'Rentier',
    FIRST_NAME: 'Prénom',
    LAST_NAME: 'Nom',
    ANNUITANT_FIRST_NAME: 'Prénom du rentier',
    ANNUITANT_LAST_NAME: 'Nom du rentier',
    POLICYOWNER_FIRST_NAME: 'Prénom du propriétaire de police',
    POLICYOWNER_LAST_NAME: 'Nom du propriétaire de police',
    POLICYOWNER_1: 'Propriétaire de police #1',
    POLICYOWNER_2: 'Propriétaire de police #2',
    POLICYOWNER_ANNUITANT_OPTION: 'Le propriétaire de police est le rentier',
    DOB: 'Date de naissance',
    SECOND_DOB: 'Date de naissance du conjoint',
    AGE: 'Âge',
    SECOND_AGE: 'Âge du conjoint',
    AGE_TOOLTIP: {
      TITLE: 'Âge conjoint',
      TEXT: "Toute condition d'âge est basée sur le plus jeune des deux anniversaires",
    },
    LIF_MAX_DISABLED_QC: 'La limite maximale de retrait pour un FRV est désactivée pour les résidents du Québec depuis 2025',
    RATE_OF_RETURN: 'Taux de rendement brut',
    AGE_CALCULATION_INFO: "L'âge est calculé en fonction de la date d'achat",
    ADD_SECOND_POLICYOWNER: 'Ajouter un propriétaire',
    ACCOUNT_DETAILS: 'Détails du compte',
    SELECT_REGISTERED_ACCOUNT: 'Sélectionnez le type de compte*',
    SELECT_PROVINCE: 'Sélectionnez la province',
    PROVINCE: 'Province',
    RIF: 'FRR (Fonds de revenu de retraite)',
    LIF: 'FRV (Fonds de revenu viager)',
    RLIF: 'FERV (Fonds enregistré de revenu viager)',
    LRIF: 'FRRI (Fonds de revenu de retraite immobilisé)',
    PRIF: 'FERRP (Fonds enregistré de revenu de retraite prescrit)',
    RSP: "REE (Régime enregistré d'épargne-retraite)",
    LIRA: 'CRI (Compte de retraite immobilisé)',
    LRSP: "REER immobilisé (Régime enregistré d'épargne-retraite immobilisé)",
    RLSP: "REIR (Régime d'épargne immobilisé restreint)",
    INVALID_DATE: 'Date invalide',
    DOB_REQUIRED: 'La date de naissance est obligatoire',
    MAX_AGE_90: "L'âge dépasse le maximum de 90 ans",
    ACCOUNT_TYPE_REQUIRED: 'Le type de compte est obligatoire',
    INVALID_ACCOUNT_TYPE_AGE_71: 'Âge maximum de 71 ans pour les comptes de type REE, CRI, REER et REIR',
    MISSING_REGISTERED_ACCOUNT: 'Veuillez sélectionner un type de compte',
    MISSING_POLICYOWNER_FNAME: 'Le prénom est requis',
    MISSING_POLICYOWNER_LNAME: 'Le nom est obligatoire',
    CLIENT_INFO: 'Informations du client',
    PURCHASE_AGE: "Âge d'achat",
    PLAN_OPTIONS: 'Options de plan',
    GIF7575: 'FPG 75/75',
    GIF75100: 'FPG 75/100',
    GIF75100PLUS: 'FPG 75/100 Plus',
    GIF100100: 'FPG 100/100',
    GIF75100PLUSINFO:
      '75/100 Plus est offert avec charge FAI à 0% de commission de dépôt et option de frais de souscription de catégorie F (prête-nom), et quatre FPG de portefeuille FNB (revenu fixe, revenu, conservateur, équilibré) et marché monétaire.',
    TERM: 'Terme',
    INVALID_TERM: "La date d'échéance dépasse l'année où le rentier atteint l'âge de 100 ans",
    DEATHGUARANTEE: 'Réinitialisation de la garantie de décès',
    DEATHGUARANTEEINFO: "Des frais supplémentaires s'appliquent",
    DEATHGUARANTEEENHANCED: 'Réinitialisation de la garantie de décès améliorée',
    DEATHGUARANTEEENHANCEDINFO: "Des frais supplémentaires s'appliquent",
    CLASS_TYPE: 'Type de classe',
    CLASS_A: 'Classe A',
    CLASS_A_PRESTIGE: 'Classe A Prestige',
    CLASS_F: 'Classe F',
    CLASS_F_PRESTIGE: 'Classe F Prestige',
    CUSTOM: 'Sur Mesure',
    PRESTIGE_INFO: 'Pour être admissible au Prestige, votre client doit détenir au moins 250 000 $ dans un ou plusieurs contrats FPG BMO émis en son nom.',
    PORTFOLIO_TYPE: 'Type de portefeuille',
    EIGHTY_TWENTY: '80/20 (Équité/Revenu Fixe)',
    SIXTY_FORTY: '60/40 (Équité/Revenu Fixe)',
    FIFTY_FIFTY: '50/50 (Équité/Revenu Fixe)',
    FORTY_SIXTY: '40/60 (Équité/Revenu Fixe)',
    TWENTY_EIGHTY: '20/80 (Équité/Revenu Fixe)',
    INCOMPLETE_PORTFOLIO_TITLE: 'Votre portefeuille est vide',
    INCOMPLETE_PORTFOLIO_BODY: 'Choisissez un plan, une classe et un type de portefeuille ci-dessus pour commencer à ajouter des fonds de BMO Assurance',
    EMPTY_PORTFOLIO: "Vous n'avez actuellement aucun avoir dans le portefeuille.",
    QUICK_SEACH_PLACEHOLDER: 'Filtrer les fonds de BMO Assurance par nom ou par code',
    ADD_HOLDING: 'Ajouter un fonds de BMO Assurance',
    VIEW_GRAPH: 'Afficher le graphique',
    CLASS_TYPE_DISABLED: 'Veuillez sélectionner une option de plan',
    PORTFOLIO_TYPE_DISABLED: 'Veuillez sélectionner une option de plan et un type de classe',
    ADDITIONAL_INFO: 'Information additionnelle',
    ADDITIONAL_INFO_EMPTY: 'Créer un portefeuille pour ajouter des informations supplémentaires',
    INITIAL_LUMP_SUM: 'Montant forfaitaire initial',
    INITIAL_PAD: 'DPA initial',
    INVALID_AMOUNT: 'Montant invalide',
    INVALID_DEPOSIT_AGE:
      'La date du dépôt initial ou les dates des dépôts subséquents dépassent les limites d’âge de ce type de compte ou de cette option de garantie.',
    INVALID_PRESTIGE_AMOUNT:
      'Remarque – Pour être admissible à la tarification de la catégorie Prestige, une somme de 250 000 $ est requise.  À titre d’exemple, vous pouvez utiliser une valeur sous ce seuil.',
    INVALID_NON_PRESTIGE_AMOUNT:
      'Remarque – Une somme supérieure à 250 000 $ est admissible à la tarification de la catégorie Prestige.  À titre d’exemple, vous pouvez utiliser une valeur au-dessus de ce seuil.',
    INVALID_DEPOSIT_AMOUNT: 'Veuillez saisir un montant de dépôt valide',
    PURCHASE_DATE: "Date d'achat",
    SUBSEQUENT_DEPOSIT: 'Dépôt ultérieur',
    YEAR_CALCULATION: 'Calcul des années:',
    IN_AGE: 'Âge',
    FROM_AGE: 'De',
    TO_AGE: 'à',
    IN_YEARS: 'Dans des années',
    NUMBER_OF_YEARS: "Nombre d'années",
    START_YEAR: "Débute à l'année",
    START_MONTH: 'Débuter au mois',
    END_MONTH: 'Fin au mois ',
    FREQUENCY: 'Fréquence',
    SELECT_FREQUENCY: 'Sélectionner la fréquence',
    WEEKLY: 'Hebdomadaire',
    BI_WEEKLY: 'Bihebdomadaire',
    MONTHLY: 'Mensuel',
    SEMI_ANNUALLY: 'Semestriellement',
    ANNUALLY: 'Annuellement',
    QUARTERLY: 'Trimestriellement',
    VOLATILITY: 'Volatilité',
    YES: 'Oui',
    NO: 'Non',
    SELECT_PROJECTION_BASIS: 'Sélectionnez la base de projection',
    'S&P 500': 'SP500/Obligations',
    'SPTSX Composite': 'SPTSX/Obligations',
    'MSCI EAFE': 'MSCI EAFE/Obligations',
    'DEX Mid Fed': 'DEX/Obligations',
    Custom: 'Sur Mesure',
    SALES_CHARGES: 'Frais de vente',
    FEL: 'FAI',
    DSC: 'FAD',
    NL3: 'Reprise de commission (2)',
    NL5: 'Reprise de commission (5)',
    ROR_AMOUNT_INFO: 'Entrez un montant compris entre 0% et 12%',
    SALES_CHARGE_AMOUNT_INFO: 'Entrez un montant compris entre 0% et 5%',
    INVALID_FROM_AGE: "L'âge de début doit être supérieure ou égale à l'âge du client à l'achat",
    INVALID_TO_AGE: "L'âge de fin doit être supérieure à l'âge de début",
    INVALID_START_YEAR: "L'année de début doit être inférieure ou égale à la durée du contrat",
    INVALID_START_MONTH: 'Le mois de début doit être inférieur ou égal au mois de fin',
    INVALID_END_MONTH: 'Le mois de fin ne peut pas être supérieur à 12',
    SCENARIOS: {
      ADVERSE_SCENARIO: 'Scénario alternatif (-2 %)',
      LUMP_SUM_WITHDRAWAL: 'MONTANT DE RETRAIT',
      SYSTEMATIC_WITHDRAWAL: 'RETRAIT SYSTÉMATIQUE',
      PORTFOLIO_VALUE: 'Valeur du portefeuille',
      NET_WITHDRAWAL: 'Retrait Net',
      NET_DEPOSIT: 'Dépôts net',
      SUBSEQUENT_DEPOSIT: 'Dépôt ultérieur',
      MATURITY_GUARANTEE: 'Garantie à l’échéance',
      DEATH_BENEFIT_GUARANTEE: 'Garantie du capital décès',
      AGE: 'Âge',
      WITHDRAWAL_SCENARIOS: 'Scénarios de retrait',
      EMPTY_WITHDRAWAL_SCENARIOS: "Cliquez sur le '+' en haut du tableau des portefeuilles pour ajouter un nouveau scénario",
      CREATE_SCENARIO: 'Ajouter un scénario de retrait',
      CREATE_DISABLED: 'Atteint un maximum de 5 scénarios',
      SCENARIO: 'Scénario',
      INCOMPLETE: 'Incomplet',
      EDIT_SCENARIO: 'Modifier le scénario',
      DELETE_SCENARIO: 'Supprimer le scénario',
      EMPTY_STATE_TITLE: 'Scénarios de retrait',
      EMPTY_STATE_MESSAGE: 'Complétez toutes les informations ci-dessus pour commencer à ajouter des scénarios de retrait.',
    },
    EDIT_SCENARIO: {
      LUMP_SUM_WITHDRAWAL: 'RETRAIT FORFAITAIRE',
      SYSTEMATIC_WITHDRAWAL: 'RETRAIT SYSTÉMATIQUE',
      MONTH: 'Mois',
      LUMP_SUM_AMOUNT: 'Montant',
      TYPE: 'Type',
      SYSTEMATIC_AMOUNT: 'Montant',
      IN_YEAR: 'Dans des années',
      AT_AGE: "À l'âge",
      START_AGE: 'Âge de départ',
      TO_AGE: "Jusqu'à",
      FREQUENCY: 'Fréquence',
      APPLY: 'Appliquer',
      INVALID_AGE: "L'âge doit être supérieur ou égal à l'âge d'achat du client",
      INVALID_AGE_65: "L'âge doit être supérieur ou égal à 65 ans",
      INVALID_AGE_CUSTOM: "L'âge doit être supérieur ou égal à {value} ans",
      INVALID_END_AGE: "L'âge de fin doit être supérieur à l'âge de début",
    },
    AGE_AND_YEARS: {
      AGE: 'âge',
      TO: 'à',
      FOR: 'pour',
      YEARS: 'ans',
      IN_YEAR: "À l'année",
      STARTING_AGE: 'Âge de départ',
      MONTH: 'mois',
      IN_MONTH: 'au mois',
      FROM_MONTH: 'du mois',
    },
    WITHDRAWAL_TYPES: {
      RRIF: 'Minimum FERR/FRV',
      LIF_MAX: 'Maximum FRV',
      CUSTOM: 'Sur Mesure',
    },
    HIDE_CONTENT_OVERLAY: {
      TITLE: 'Portefeuille non entièrement alloué',
      MESSAGE: 'Veuillez vous assurer que votre portefeuille est alloué à 100% avant de continuer.',
    },
    GIF_GRAPH: {
      EDIT_DETAILS: 'Modifier les détails',
      EMPTY_STATE_TITLE: 'Choisissez un scénario',
      EMPTY_STATE_MESSAGE: 'Sélectionnez un scénario ci-dessous pour afficher le graphique',
      LEGEND: {
        MATURITY_GUARANTEE: 'À l’échéance: 75 % des dépôts, moins un montant réduit en proportion des retraits effectués.',
        MATURITY_GUARANTEE_100100:
          'À l’échéance: Tous les dépôts effectués au moins 15 ans avant la date d’échéance et 75 % des dépôts effectués moins de 15 ans avant la date d’échéance. Les réinitialisations mensuelles automatiques du capital garanti à l’échéance sont effectuées jusqu’à la date inclusivement tombant 10 ans avant la date d’échéance.',
        DEATH_BENEFIT_GUARANTEE_7575: ' Au décès: 75 % des dépôts, moins un montant réduit en proportion des retraits effectués',
        DEATH_BENEFIT_GUARANTEE_75100:
          ' Au décès: Tous les dépôts effectués avant le 80e anniversaire du rentier et 75 % des dépôts effectués à compter de 80 ans, moins un montant réduit en proportion des retraits effectués. Automatiquement les réinitialisations ont lieu automatiquement tous les trois ans à la date d’anniversaire de la police jusqu’au dernier anniversaire de la police, inclusivement, précédant le 80e anniversaire du rentier.',
        DEATH_BENEFIT_GUARANTEE_75100PLUS:
          ' Au décès: Tous les dépôts effectués avant le 85e anniversaire de naissance du rentier et 75 % des dépôts effectués à compter de 85 ans, moins un montant réduit en proportion des retraits effectués.',
        DEATH_BENEFIT_GUARANTEE_100100:
          ' Au décès: Tous les dépôts effectués avant le 80e anniversaire du rentier et 75 % des dépôts effectués à compter de 80 ans, moins un montant réduit en proportion des retraits effectués. Si l’option de réinitialisation du capital garanti au décès est sélectionnée, automatiquement les réinitialisations ont lieu automatiquement tous les trois ans à la date d’anniversaire de la police jusqu’au dernier anniversaire de la police, inclusivement, précédant le 80e anniversaire du rentier. Des frais supplémentaires s’appliquent.',
      },
      DEPOSIT: 'Les dépôts subséquents sont nets de frais.',
    },
    PROPOSAL: {
      GIF_ILLUSTRATION: 'Illustration FPG',
      TITLE: 'Fonds de placement garanti BMO',
      HEADERS: {
        ACCOUNT_TYPE: 'Type de compte',
        PERSONAL_DATA: 'Renseignements personnels',
        DEPOSIT_DETAILS: 'Détails sur le dépôt initial',
        INVESTMENT_ASSUMPTIONS: 'Hypothèses de placement',
        ANNUITANT_NAME: 'Nom du rentier',
        DOB: 'Date de naissance',
        POLICYOWNERS: 'Propriétaires de police',
        AGE: 'Âge au moment de l’achat',
        AMOUNT: 'Montant',
        PURCHASE_DATE: 'Date d’achat',
        GUARANTEE_LEVEL: 'Niveau de garantie',
        CLASS: 'Catégorie',
        FUNDS: 'Fonds',
        MER: 'RFG',
        DGRO: 'Avenant de la prestation de décès',
        TERM: 'Durée',
        MATURITY: 'Date d’échéance',
        DEATH_BENEFIT_RESET: 'Réinitialisation du capital garanti au décès',
        SALES_CHARGE: 'Option de frais d’acquisition',
        FUND_RETURN: 'Rendement du Fonds',
        WITHDRAWAL_AMOUNT: 'Montant de retrait',
        LUMP_SUM_WITHDRAWAL_AMOUNT: 'Montant du retrait forfaitaire',
        SYSTEMATIC_WITHDRAWAL_AMOUNT: 'Montant du retrait systématique',
        DATE: 'Date',
        ROR: 'Taux de rendement*',
      },
      GIF7575: '75/75',
      GIF75100: '75/100',
      GIF75100PLUS: '75/100 Plus',
      GIF100100: '100/100',
      CLASS_A: 'Catégorie A',
      CLASS_A_PRESTIGE: 'Catégorie A Prestige*',
      CLASS_F: 'Catégorie F',
      CLASS_F_PRESTIGE: 'Catégorie F Prestige*',
      FEL: 'Frais d’acquisition initiaux (FAI)',
      DSC: 'Frais d’acquisition différés (FAD)',
      NL3: 'Reprise de commission (2)',
      NL5: 'Reprise de commission (5)',
      DGR_ENHANCED: ' (avec Amélioration)',
      PRESTIGE_DISCLAIMER:
        '** Minimum de 250 000 $ d’actifs pour être admissible à la catégorie Prestige.  Ces actifs comprennent les contrats de fonds distincts émis par BMO Assurance détenus au nom du client.',
      BASE_SCENARIO: 'Scénario de base',
      EQUITY: 'Équité',
      BOND: 'Obligation',
      NONE: 'Aucun',
      OTHERS: 'Autres',
      DISCLAIMER:
        'Cette illustration réalisée par CapIntel Inc. n’est pas un contrat. En cas de divergence entre une illustration qui vous a été fournie et votre contrat avec BMO Assurance, ce dernier prévaut. Les données relatives aux fonds sous-jacents, y compris, mais sans s’y limiter, les rendements historiques, sont fournies à BMO Assurance par CapIntel Inc., un fournisseur de technologie financière, à partir de données obtenues de tiers (notamment Refinitiv, Fundata et Barcharts) et leur exactitude ne peut être garantie.',
      ROR_DISCLAIMER:
        '* Le taux de rendement sert uniquement à établir des projections et se fonde sur un taux fixe sélectionné ou les volets actions et revenu fixe des FPG BMO sélectionnés et les rendements historiques de l’indice boursier choisi. Le taux de rendement projeté est établi après déduction des frais. Les rendements historiques ne sont pas un indicateur des rendements futurs. Les valeurs illustrées ne sont pas garanties et on ne doit pas s’y fier indûment. L’illustration des projections ne sert qu’à démontrer la fonctionnalité des caractéristiques des FPG BMO. Toute somme affectée à un fonds distinct est placée aux risques du propriétaire de la police, et sa valeur peut fluctuer à la hausse comme à la baisse. BMO Société d’assurance-vie est le seul émetteur et garant du contrat d’assurance individuelle à capital variable FPG BMO. Ce document fournit des renseignements généraux. Pour obtenir de l’information détaillée sur les FPG BMO, veuillez consulter les dispositions de la police et la notice explicative.',
      MARKETING_TEMPLATE: {
        DISCLAIMERS: {
          FUND_CONSTRUCTION: {
            BACKTESTED:
              '* Source : CapIntel, au {{date}}. Les rendements passés ne sont pas un indicateur des rendements futurs. Les rendements estimatifs du FPG BMO sont calculés à l’aide du rendement net du {{fundName}} et en réduisant ce rendement net par la différence entre le RFG du fonds sous-jacent et le RFG actuel du {{gifName}}. Date d’entrée en activité – {{gifName}} – {{inception}}. ',
            REGULAR:
              '* Source : CapIntel, au {{date}}. Les rendements passés ne sont pas un indicateur des rendements futurs. Les rendements historiques du {{gifName}} ont été utilisés. Date d’entrée en activité du {{gifName}} – {{inception}}.',
          },
          BENCHMARK: {
            GIC: 'Comparaison du rendement du Fonds et du taux historique d’un CPG à 5 ans moyen sur 5 année(s).  Source CapIntel.',
            MANUAL_RATE: 'Comparaison du fonds et d’un taux annuel précis de {{rate}} %.',
          },
          GIF100100:
            'Option FPG 100/100 – Les réinitialisations mensuelles automatiques du capital garanti à l’échéance sont effectuées chaque mois précédant d’au moins 10 ans la date d’échéance. Les Fonds n’offrent pas tous le FPG 100/100.',
          GENERIC:
            'Toute somme affectée à un fonds distinct est placée aux risques du propriétaire de la police. BMO Société d’assurance-vie est le seul émetteur et garant du contrat d’assurance individuelle à capital variable FPG BMO. Ce document fournit des renseignements généraux. Pour obtenir de l’information détaillée sur les FPG BMO, veuillez consulter les dispositions de la police et la notice explicative.',
        },
      },
    },
    VERSION_NUMBER: 'FPGBMO V5.0/25-12',
  },
} as Dictionary<any>;

export const MIN_RRIF_AGE = 55; // TODO - bug, should be 65. To confirm.
export const MIN_LIF_AGE = 50;

export const MAX_RRIF_AGE = 95;
export const MAX_LIF_AGE = 95;

export const MAX_REGISTERED_DEPOSIT = 71;

export const MAX_DEATH_BENEFIT_AGE_75PLUS = 85;
export const MAX_DEATH_BENEFIT_AGE = 80;

export const GIF_CLASS_TYPES: Dictionary<string> = {
  CLASS_A: 'Class A',
  CLASS_A_PRESTIGE: 'Class A Prestige',
  CLASS_F: 'Class F',
  CLASS_F_PRESTIGE: 'Class F Prestige',
};

export const GIF_PLAN_OPTIONS: Dictionary<string> = {
  GIF7575: 'GIF 75/75',
  GIF75100: 'GIF 75/100',
  GIF75100PLUS: 'GIF 75/100 Plus',
  GIF100100: 'GIF 100/100',
};

export const maxAge71Accounts = [REGISTERED_ACCOUNT_TYPES.RSP, REGISTERED_ACCOUNT_TYPES.LIRA, REGISTERED_ACCOUNT_TYPES.LRSP, REGISTERED_ACCOUNT_TYPES.RLSP];

const AGE_BY_PLAN = {
  [GIF_PLAN_OPTIONS.GIF7575]: 90,
  [GIF_PLAN_OPTIONS.GIF75100]: 80,
  [GIF_PLAN_OPTIONS.GIF75100PLUS]: 85,
  [GIF_PLAN_OPTIONS.GIF100100]: 85,
};

export const MAX_AGE_NON_REGISTERED_DEPOSIT = {
  [GIF_PLAN_OPTIONS.GIF7575]: 90,
  [GIF_PLAN_OPTIONS.GIF75100]: 80,
  [GIF_PLAN_OPTIONS.GIF75100PLUS]: 85,
  [GIF_PLAN_OPTIONS.GIF100100]: 85,
};

export const INCOME_ACCOUNTS_DEPOSIT = {
  [REGISTERED_ACCOUNT_TYPES.RIF]: AGE_BY_PLAN,
  [REGISTERED_ACCOUNT_TYPES.LIF]: AGE_BY_PLAN,
  [REGISTERED_ACCOUNT_TYPES.LRIF]: AGE_BY_PLAN,
  [REGISTERED_ACCOUNT_TYPES.PRIF]: AGE_BY_PLAN,
  [REGISTERED_ACCOUNT_TYPES.RLIF]: AGE_BY_PLAN,
};

export const GIF_PORTFOLIO_TYPES: Dictionary<string> = {
  CUSTOM: 'Custom',
  TWENTY_EIGHTY: '20/80 (Equity/FI)',
  FORTY_SIXTY: '40/60 (Equity/FI)',
  FIFTY_FIFTY: '50/50 (Equity/FI)',
  SIXTY_FORTY: '60/40 (Equity/FI)',
  EIGHTY_TWENTY: '80/20 (Equity/FI)',
};

export const GIF_DEPOSIT_TYPES = {
  LUMP_SUM: 'initialLumpSum',
  PAD: 'initialPAD',
};

export const GIF_SCHEME_TYPES = {
  YEAR: 'year',
  AGE: 'age',
};

export const SALES_CHARGES: Dictionary<string> = {
  FEL: 'Front-end load',
  DSC: 'Deferred sales charge',
  NL3: 'No load 3%',
  NL5: 'No load 5%',
};

export const MIN_LIF_RRIF_RATES: Dictionary<number> = {
  50: 2.5,
  51: 2.56,
  52: 2.63,
  53: 2.7,
  54: 2.78,
  55: 2.86,
  56: 2.94,
  57: 3.03,
  58: 3.13,
  59: 3.23,
  60: 3.33,
  61: 3.45,
  62: 3.57,
  63: 3.7,
  64: 3.85,
  65: 4.0,
  66: 4.17,
  67: 4.35,
  68: 4.55,
  69: 4.76,
  70: 5.0,
  71: 5.28,
  72: 5.4,
  73: 5.53,
  74: 5.67,
  75: 5.82,
  76: 5.98,
  77: 6.17,
  78: 6.36,
  79: 6.58,
  80: 6.82,
  81: 7.08,
  82: 7.38,
  83: 7.71,
  84: 8.08,
  85: 8.51,
  86: 8.99,
  87: 9.55,
  88: 10.21,
  89: 10.99,
  90: 11.92,
  91: 13.06,
  92: 14.49,
  93: 16.34,
  94: 18.79,
  95: 20.0,
};

export const DSC_SCHEDULE: Dictionary<number> = {
  0: 5,
  1: 5,
  2: 5,
  3: 4,
  4: 4,
  5: 3,
  6: 2,
  7: 0,
};

export const WITHDRAWAL_TYPES: Dictionary<string> = {
  CUSTOM: 'Custom',
  RRIF: 'RRIF',
  LIF_MAX: 'LIF_MAX',
};

export enum INVALID_AGE_REASON {
  INITIAL_DEPOSIT = 'PURCHASE_AGE',
  SUBSEQUENT_DEPOSIT = 'SUBSEQUENT_DEPOSIT',
}

type GraphKey = 'portfolioValue' | 'netWithdrawal' | 'deathBenefitGuarantee' | 'maturityGuarantee' | 'netDeposit' | 'age';

type GIFGraphConfig = {
  key: GraphKey;
  color: string;
  i18nKey: string;
  hidden?: boolean;
  tooltip?: Dictionary<{
    valueSuffix?: string;
    valuePrefix?: string;
    valueDecimals?: number;
  }>;
};

export const GIF_GRAPH_CONFIGS: Dictionary<GIFGraphConfig> = {
  PORTFOLIO_VALUE: {
    key: 'portfolioValue',
    color: '#b153ff',
    i18nKey: 'PORTFOLIO_VALUE',
    tooltip: {
      'fr-CA': { valueSuffix: '$', valueDecimals: 2 },
      'en-CA': { valuePrefix: '$', valueDecimals: 2 },
    },
  },
  NET_WITHDRAWAL: {
    key: 'netWithdrawal',
    color: '#3a9779',
    i18nKey: 'NET_WITHDRAWAL',
    tooltip: {
      'fr-CA': { valueSuffix: '$', valueDecimals: 2 },
      'en-CA': { valuePrefix: '$', valueDecimals: 2 },
    },
  },
  SUBSEQUENT_DEPOSIT: {
    key: 'netDeposit',
    color: '#ff8300',
    i18nKey: 'SUBSEQUENT_DEPOSIT',
    tooltip: {
      'fr-CA': { valueSuffix: '$', valueDecimals: 2 },
      'en-CA': { valuePrefix: '$', valueDecimals: 2 },
    },
  },
  MATURITY_GUARANTEE: {
    key: 'maturityGuarantee',
    color: '#e43e89',
    i18nKey: 'MATURITY_GUARANTEE',
    tooltip: {
      'fr-CA': { valueSuffix: '$', valueDecimals: 2 },
      'en-CA': { valuePrefix: '$', valueDecimals: 2 },
    },
  },
  DEATH_BENEFIT_GUARANTEE: {
    key: 'deathBenefitGuarantee',
    color: '#2e8eff',
    i18nKey: 'DEATH_BENEFIT_GUARANTEE',
    tooltip: {
      'fr-CA': { valueSuffix: '$', valueDecimals: 2 },
      'en-CA': { valuePrefix: '$', valueDecimals: 2 },
    },
  },
};

export const GIF_TABLE_CONFIGS: Dictionary<GIFGraphConfig> = {
  AGE: {
    key: 'age',
    color: '#2eff9f',
    i18nKey: 'AGE',
    tooltip: {
      'en-CA': { valueDecimals: 0 },
      'fr-CA': { valueDecimals: 0 },
    },
  },
  ...GIF_GRAPH_CONFIGS,
};

export const pg1MaxRowsWithChart = 9;
export const pg2MaxRowsWithChart = 11;
export const pg3MaxRowsWithChart = 11;

export enum PROJECTION_BASIS_TYPES {
  CUSTOM = 'custom',
  BENCHMARK = 'benchmark',
}

export const ADVERSE_SCENARIO_RATE = 2;
